import { PropsWithChildren } from "react";
import { StudentFormData, useStudentForm } from "../StudentDataFormProvider";
import { Stack, Typography } from "@mui/material";

type FileTypes = "document" | "image";

type Props = {
  fieldName: keyof StudentFormData;
  label?: string;
  required?: boolean;
  fileType?: FileTypes;
  dependsOn?: keyof StudentFormData;
};

export const FileUploadField = ({
  fieldName,
  label,
  required = false,
  fileType = "document",
  dependsOn,
}: PropsWithChildren<Props>) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useStudentForm();

  const dependency = dependsOn && watch(dependsOn);
  const isRequired = Boolean(dependency || required);

  return (
    <Stack gap={"0.25rem"} width={"100%"}>
      {label && <label htmlFor={fieldName}>{label}</label>}
      <input
        type="file"
        accept={getValidFileTypes(fileType).join(",")}
        {...register(fieldName, {
          required: isRequired,
          validate: {
            correctFileType: (file) => validateFileType(file, fileType, isRequired),
            lessThan5MB: (file) => validateFileSize(file, isRequired),
          },
        })}
      ></input>
      {errors[fieldName] && (
        <Typography variant="caption" sx={{ color: "red" }}>
          {errors[fieldName]?.message}
        </Typography>
      )}
    </Stack>
  );
};

const validateFileSize = (
  files: string | boolean | Date | File | FileList | undefined,
  isRequired: boolean
) => {
  if (!(files instanceof FileList) && !(files instanceof File)) return "Please upload a valid file.";
  if (files instanceof FileList && files.length > 1) return "Too many files selected.";
  if (((files instanceof FileList && files.length === 0) || files == null) && isRequired)
    return "This field is required.";
  const file = files instanceof FileList ? files[0] : files;
  if (file == null && !isRequired) return true;

  return file.size < 2000000 || "File must be lower than 2MB.";
};

const validateFileType = (
  files: string | boolean | Date | File | FileList | undefined,
  fileType: FileTypes,
  isRequired: boolean
) => {
  if (!(files instanceof FileList) && !(files instanceof File)) return "Please upload a valid file.";
  if (files instanceof FileList && files.length > 1) return "Too many files selected.";
  if (((files instanceof FileList && files.length === 0) || files == null) && isRequired)
    return "This field is required.";

  const file = files instanceof FileList ? files[0] : files;
  if (file == null && !isRequired) return true;
  const validDocumentFileTypes = getValidFileTypes(fileType);

  return validDocumentFileTypes.includes(file.type) || "The uploaded file type is not allowed.";
};

const getValidFileTypes = (fileType: FileTypes) => {
  switch (fileType) {
    case "document":
      return VALID_DOCUMENT_TYPES;
    case "image":
      return VALID_IMAGE_TYPES;
    default:
      return VALID_DOCUMENT_TYPES;
  }
};

const VALID_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "image/heif",
];

export const VALID_DOCUMENT_TYPES = ["application/pdf", ...VALID_IMAGE_TYPES];
